import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { PageMetaData } from "@/react/components/Layout/PageMetaData";
import { SettingsHeader } from "@/react/components/SettingsApp/SettingsHeader";
import { UpgradePlan } from "@circle-react/components/LiveStreams/UpgradePlan";
import { GoLive } from "@circle-react/components/Spaces/SidebarV2/SidebarFooter";
import { usePunditUserContext } from "@circle-react/contexts";
import { useLiveStreamsFeatureFlags } from "@circle-react/hooks/useLiveStreamsFeatureFlags";
import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { DataTable } from "@circle-react-shared/uikit/DataTableV2";
import { useToast } from "@circle-react-uikit/ToastV2";
import { SettingsContainer } from "../SettingsContainer";
import { Limits } from "./Limits";
import { LiveEmptyState } from "./LiveEmptyState";
import { LiveSettingsPagination } from "./LiveSettingsPagination";
import { LiveStreamsTable } from "./LiveStreamsTable";
import { useGetLiveStreamRoom, useLiveStreamRoomsApi } from "./hooks";

interface LiveStreamSettingsProps {
  hideHeader?: boolean;
}

export const LiveStreamSettings = ({
  hideHeader = false,
}: LiveStreamSettingsProps) => {
  const history = useHistory();
  const location = useLocation();
  const { error: toastError } = useToast();

  const {
    currentCommunity: {
      supports_live_streams: doesCommunitySupportsLiveStreams,
    } = {},
  } = usePunditUserContext();
  const { isLiveStreamsLimitsEnabled } = useLiveStreamsFeatureFlags();
  const {
    records,
    page,
    hasNextPage,
    totalRecordCount,
    startRecordNumber,
    endRecordNumber,
    isLoading,
    refetch,
    fetchNextPage,
    fetchPrevPage,
  } = useLiveStreamRoomsApi();

  const {
    room: currentRoom,
    isLoading: isLoadingRoom,
    error: roomError,
    roomId,
  } = useGetLiveStreamRoom();

  const hasLiveRecords = totalRecordCount > 0;

  useEffect(() => {
    if (roomError && roomId) {
      const urlParams = new URLSearchParams(location.search);
      const search = urlParams.toString();
      history.push({
        pathname: "/settings/live_streams",
        search: search ? `?${search}` : "",
      });
      toastError(t("error"));
    }
  }, [roomError, roomId, history, location.search, toastError]);

  const handleCloseModal = () => {
    const urlParams = new URLSearchParams(location.search);
    const search = urlParams.toString();
    history.push({
      pathname: "/settings/live_streams",
      search: search ? `?${search}` : "",
    });
  };

  return (
    <div className="bg-primary h-full">
      {hideHeader ? (
        <PageMetaData title={t("live_streams_settings")} />
      ) : (
        <SettingsHeader
          pageTitle={t("live_streams_settings")}
          rightContent={<GoLive inSettings />}
          hasBorder
        />
      )}
      <SettingsContainer maxWidth="none">
        {doesCommunitySupportsLiveStreams && (
          <>
            {isLoading ? (
              <LoaderRow />
            ) : (
              <>
                {isLiveStreamsLimitsEnabled && hasLiveRecords && <Limits />}
                {totalRecordCount > 0 && (
                  <DataTable.Headline className="!px-0">
                    <DataTable.Headline.Title>
                      {t("live_streams.count", {
                        count: totalRecordCount,
                      })}
                    </DataTable.Headline.Title>
                  </DataTable.Headline>
                )}
                {hasLiveRecords ? (
                  <>
                    <LiveStreamsTable
                      records={records}
                      refetch={refetch}
                      className="mb-7"
                      currentRoom={currentRoom}
                      isLoadingRoom={isLoadingRoom}
                      onCloseModal={handleCloseModal}
                    />
                    <LiveSettingsPagination
                      startRecordNumber={startRecordNumber}
                      endRecordNumber={endRecordNumber}
                      totalRecordCount={totalRecordCount}
                      page={page}
                      fetchPrevPage={fetchPrevPage}
                      hasNextPage={hasNextPage}
                      fetchNextPage={fetchNextPage}
                    />
                  </>
                ) : (
                  <LiveEmptyState />
                )}
              </>
            )}
          </>
        )}
        {!doesCommunitySupportsLiveStreams && (
          <div className="border-primary bg-primary border p-6 text-center lg:rounded-lg lg:p-36">
            <UpgradePlan />
          </div>
        )}
      </SettingsContainer>
    </div>
  );
};

export default LiveStreamSettings;
